import { toastStore } from '@/toast/store';
import { ApiError } from './api';

/**
 * Central, unified toaster interface for API errors (and the success/info
 * counterparts that go with it).
 *
 * Surface this as the single channel for transient user feedback in the SPA:
 *  - Mutations and queries get an auto-fire path via the TanStack Query cache
 *    handlers in `query-client.ts` — any unhandled `ApiError` becomes a toast.
 *    Callsites that own their own inline error UI (e.g. the Settings
 *    page-level banner, form-field errors) opt out via
 *    `meta: { silentError: true }`.
 *  - Imperative flows that catch errors themselves (e.g. OAuth redirect,
 *    fire-and-forget actions) call `notifyApiError(err, fallback)` directly.
 *
 * These helpers feed the hand-rolled toaster store in `src/toast/` (the same
 * store the `toast` singleton in `toast/useToast.ts` writes to). They live
 * here, separate from that module, so non-React module code — notably the
 * TanStack Query cache handlers in `query-client.ts` — can fire toasts
 * without a React context.
 *
 * 401 "Session expired" is intentionally skipped — the fetch wrapper in
 * `lib/api.ts` only throws that after it has already forced logout, and
 * toasting on top of the redirect produces a confusing flash.
 */

// Toasts are a single line only — there is no description/body. `duration` is
// in seconds (the store's `dur`); omit it to use the default.
type ToastOptions = {
  duration?: number;
};

const SESSION_EXPIRED_MESSAGE = 'Session expired';

// Backend throw sites use UPPER_SNAKE_CASE machine codes (e.g. `DNS_CHECK_REQUIRED`,
// `EMAIL_ALREADY_CONNECTED`). Surfacing those verbatim in a toast reads as a stack
// trace to the user. Convert to a sentence — first word capitalised, the rest lower:
//   "DNS_CHECK_REQUIRED" → "DNS check required"
//   "EMAIL_ALREADY_CONNECTED" → "Email already connected"
// Strings that aren't a machine code (have lowercase letters or spaces) pass through.
const MACHINE_CODE = /^[A-Z][A-Z0-9_]*$/;

// Some throw sites encode `CODE: human suffix` — humanize the code half and keep
// the suffix as-is (e.g. `DNS_CHECK_REQUIRED: SPF, DKIM are missing for foo.com`).
const MACHINE_CODE_WITH_SUFFIX = /^([A-Z][A-Z0-9_]*):\s*(.+)$/;

// Tokens to preserve in their original case rather than lowercasing them when
// expanding a snake-case code. Add sparingly — these are the acronyms our throw
// sites actually use.
const ACRONYMS = new Set(['DNS', 'SMTP', 'IMAP', 'OAUTH', 'SPF', 'DKIM', 'DMARC', 'MX', 'URL', 'API', 'ID', 'SH', 'CSV', 'ESP', 'HMAC']);

function humanizeCode(code: string): string {
  const parts = code.split('_').filter(Boolean);
  if (parts.length === 0) return code;
  const [first, ...rest] = parts;
  const head = ACRONYMS.has(first)
    ? first
    : first.charAt(0) + first.slice(1).toLowerCase();
  const tail = rest.map((p) => (ACRONYMS.has(p) ? p : p.toLowerCase()));
  return [head, ...tail].join(' ');
}

function humanizeMessage(raw: string): string {
  if (MACHINE_CODE.test(raw)) return humanizeCode(raw);
  const m = MACHINE_CODE_WITH_SUFFIX.exec(raw);
  if (m) return `${humanizeCode(m[1])}: ${m[2]}`;
  return raw;
}

function resolveMessage(err: unknown, fallback: string): string | null {
  if (err instanceof ApiError) {
    if (err.status === 401 && err.message === SESSION_EXPIRED_MESSAGE) return null;
    return humanizeMessage(err.message || fallback);
  }
  if (err instanceof Error && err.message) return humanizeMessage(err.message);
  return fallback;
}

export function notifyApiError(
  err: unknown,
  fallback = 'Something went wrong',
  options?: ToastOptions,
): void {
  const message = resolveMessage(err, fallback);
  if (message === null) return;
  toastStore.add({ tone: 'bad', message, dur: options?.duration });
}

export function notifySuccess(message: string, options?: ToastOptions): void {
  toastStore.add({ tone: 'good', message, dur: options?.duration });
}

export function notifyWarning(message: string, options?: ToastOptions): void {
  toastStore.add({ tone: 'warn', message, dur: options?.duration });
}
